// Load a feature only where its markup is, inside one page's container.
//
// Every page shares one entry bundle, but a keyword page has no carousel and
// the blog has no video player. Each heavy feature lives in its own module
// behind a dynamic import(), and these two helpers decide when to fetch it:
//
//   mountWhenPresent: as soon as the container has the markup (the feature is
//                     visible content, so it should be live at once)
//   mountWhenNear:    when the markup comes within a screen of the viewport,
//                     or is touched or focused first (for a lead form at the
//                     foot of a page: ready before it is seen, never loaded
//                     for a visitor who leaves from the top)
//
// Both return a function that stands the feature down: it cancels a load that
// has not finished, disconnects the observer, and calls whatever the feature's
// mount returned. The page lifecycle (./page.js) calls it when the page's
// content is about to be discarded, so a module that arrives after that point
// is never mounted into a container that is gone.
//
// `load` is the () => import("...") call; `mount` receives the module and may
// return its own teardown.

export function mountWhenPresent(root, selector, load, mount) {
  if (!root.querySelector(selector)) return noop;
  return loadThenMount(load, mount);
}

export function mountWhenNear(root, selector, load, mount) {
  const targets = root.querySelectorAll(selector);
  if (!targets.length) return noop;

  const controller = new AbortController();
  let teardown = noop;
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    controller.abort(); // the observer and the touch listeners are no longer needed
    teardown = loadThenMount(load, mount);
  };

  if (!("IntersectionObserver" in window)) {
    start();
    return () => teardown();
  }

  const observer = new IntersectionObserver(
    (entries) => entries.some((entry) => entry.isIntersecting) && start(),
    { rootMargin: "100% 0px" },
  );
  controller.signal.addEventListener("abort", () => observer.disconnect());
  targets.forEach((el) => {
    observer.observe(el);
    el.addEventListener("focusin", start, { once: true, signal: controller.signal });
    el.addEventListener("pointerdown", start, { once: true, signal: controller.signal });
  });

  return () => {
    controller.abort();
    teardown();
  };
}

// Imports the module and mounts it, unless stood down first.
function loadThenMount(load, mount) {
  let live = true;
  let teardown = noop;
  load().then((module) => {
    if (live) teardown = mount(module) || noop;
  });
  return () => {
    live = false;
    teardown();
  };
}

function noop() {}
